import type {
  LiveEffectEvent,
  LiveEffectOverlay,
} from '../../api/payment/live-effect.api';
import type { ObsError } from './obs-client';
import type { ObsRunner } from './page-live-effect-livestream.component';

export type ObsCall = (
  type: string,
  data?: Record<string, unknown>
) => Promise<any>;
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const SNAPSHOT_KEY = 'cab-effect-snapshot';
const FILTER_NAME = 'Cab hiệu ứng';
const MAX_MS = 15_000;

type Transform = {
  positionX: number;
  positionY: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  alignment: number;
  width: number;
  height: number;
  boundsType?: string;
  boundsWidth?: number;
  boundsHeight?: number;
};
type Snapshot =
  | {
      kind: 'transform';
      sceneName: string;
      sceneItemId: number;
      transform: Transform;
    }
  | { kind: 'filter'; sourceName: string }
  | { kind: 'mute'; inputName: string; wasMuted: boolean }
  | { kind: 'scene'; sceneName: string };

const TRANSFORM_CODES = [
  'quake',
  'upside_down',
  'mirror',
  'spin',
  'drunk',
  'shrink',
  'zoom',
  'stretch',
];
const FILTER_CODES = ['rainbow', 'grayscale', 'blackout'];

const isBounded = (t: Transform) =>
  !!t.boundsType && t.boundsType !== 'OBS_BOUNDS_NONE';

/** Kích thước hiện trên khung: khi có khung bao (bounds) thì lấy khung bao, không thì lấy width/height (đã nhân tỉ lệ, có thể âm khi lật). */
const sizeOf = (t: Transform) =>
  isBounded(t)
    ? { w: Math.abs(t.boundsWidth ?? 0), h: Math.abs(t.boundsHeight ?? 0) }
    : { w: Math.abs(t.width), h: Math.abs(t.height) };

/** Chỉ ghi các trường đặt được (width/height là chỉ đọc trong OBS). */
const writable = (t: Transform) => {
  const base: Record<string, unknown> = {
    positionX: t.positionX,
    positionY: t.positionY,
    rotation: t.rotation,
    scaleX: t.scaleX,
    scaleY: t.scaleY,
    alignment: t.alignment,
  };
  if (isBounded(t)) {
    base.boundsWidth = t.boundsWidth;
    base.boundsHeight = t.boundsHeight;
  }
  return base;
};

/**
 * Đổi neo về tâm (alignment 0) để xoay, phóng quanh tâm hình. Bit OBS: trái 1, phải 2, trên 4, dưới 8.
 * Không có khung bao thì OBS tính gốc neo theo kích thước CÓ DẤU (hình lật có width/height âm), nên giữ nguyên dấu;
 * khung bao thì luôn dương.
 */
const toCenter = (t: Transform): Transform => {
  const { w, h } = isBounded(t) ? sizeOf(t) : { w: t.width, h: t.height };
  /* eslint-disable no-bitwise -- alignment của OBS là cờ bit */
  const dx = t.alignment & 1 ? w / 2 : t.alignment & 2 ? -w / 2 : 0;
  const dy = t.alignment & 4 ? h / 2 : t.alignment & 8 ? -h / 2 : 0;
  /* eslint-enable no-bitwise */
  return {
    ...t,
    alignment: 0,
    positionX: t.positionX + dx,
    positionY: t.positionY + dy,
  };
};

/** OBS báo không tìm thấy (nguồn, cảnh, bộ lọc đã bị xoá hẳn): trả hình cũng vô ích. */
const isNotFound = (e: unknown) => (e as ObsError | null)?.code === 600;

type Stored = Snapshot & { runId?: string };

export const createObsEffects = (opts: {
  call: ObsCall;
  storage: StorageLike;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  frameMs?: number;
  /** Trạng thái thật của kết nối OBS; bỏ trống thì coi như đang nối. */
  isConnected?: () => boolean;
}) => {
  const { call, storage } = opts;
  const now = opts.now ?? (() => Date.now());
  const sleep =
    opts.sleep ??
    ((ms: number) =>
      new Promise<void>((r) => {
        window.setTimeout(r, ms);
      }));
  const frameMs = opts.frameMs ?? 50;
  /** Mỗi lần reset hay có trò mới thì tăng; trò đang chạy thấy khác số của mình thì dừng. */
  let generation = 0;
  let runSeq = 0;
  /** Các lượt đang chạy (bản lưu của chúng không bị lượt khác trả hình hay xoá). */
  const liveRuns = new Set<string>();

  const readStored = (): Stored | null => {
    const raw = storage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Stored;
    } catch {
      storage.removeItem(SNAPSHOT_KEY);
      return null;
    }
  };
  const save = (runId: string, s: Snapshot) =>
    storage.setItem(SNAPSHOT_KEY, JSON.stringify({ ...s, runId }));
  /** Chỉ xoá khi bản đang lưu vẫn là của lượt này (lượt cũ không xoá được bản của lượt mới). */
  const clearIfOwner = (runId?: string) => {
    const cur = readStored();
    if (cur && cur.runId === runId) storage.removeItem(SNAPSHOT_KEY);
  };

  const restore = async (s: Snapshot) => {
    if (s.kind === 'transform')
      await call('SetSceneItemTransform', {
        sceneName: s.sceneName,
        sceneItemId: s.sceneItemId,
        sceneItemTransform: writable(s.transform),
      });
    if (s.kind === 'filter')
      await call('SetSourceFilterEnabled', {
        sourceName: s.sourceName,
        filterName: FILTER_NAME,
        filterEnabled: false,
      });
    if (s.kind === 'mute')
      await call('SetInputMute', {
        inputName: s.inputName,
        inputMuted: s.wasMuted,
      });
    if (s.kind === 'scene')
      await call('SetCurrentProgramScene', { sceneName: s.sceneName });
  };

  /** Trả hình theo bản lưu. Chỉ xoá bản lưu khi trả xong, hoặc khi OBS báo thứ cần trả đã bị xoá hẳn; lỗi khác thì giữ để lần sau trả tiếp. */
  const restoreStored = async (s: Stored) => {
    try {
      await restore(s);
    } catch (e) {
      if (!isNotFound(e)) throw e;
    }
    clearIfOwner(s.runId);
  };

  const restoreSnapshot = async () => {
    const s = readStored();
    if (s) await restoreStored(s);
  };

  /** Trước trò mới: trả bản lưu sót lại (trang mở lại, OBS rớt lúc trả hình), trừ bản của lượt còn đang chạy. */
  const restoreLeftover = async () => {
    const s = readStored();
    if (s && !(s.runId && liveRuns.has(s.runId))) await restoreStored(s);
  };

  type Ctx = { runId: string; aborted: () => boolean };

  /**
   * Lưu bản gốc TRƯỚC khi đổi gì trong OBS, áp thay đổi, chạy frame(t, giây) tới hết thời lượng (tính theo đồng hồ)
   * hoặc tới khi bị reset; luôn trả hình (kể cả khi lỗi). Trả không được thì bản lưu còn đó cho lần nối lại.
   */
  const perform = async (
    ctx: Ctx,
    durationSec: number,
    snapshot: Snapshot,
    apply: () => Promise<unknown>,
    frame: (t: number, elapsedSec: number) => Promise<void> = async () =>
      undefined
  ) => {
    liveRuns.add(ctx.runId);
    save(ctx.runId, snapshot);
    const totalMs = Math.min(MAX_MS, Math.max(3, durationSec) * 1000);
    try {
      if (ctx.aborted()) return;
      await apply();
      const start = now();
      for (;;) {
        if (ctx.aborted()) break;
        const elapsedMs = now() - start;
        if (elapsedMs > totalMs) break;
        // Từng khung phải chạy lần lượt.
        // eslint-disable-next-line no-await-in-loop
        await frame(Math.min(1, elapsedMs / totalMs), elapsedMs / 1000);
        // eslint-disable-next-line no-await-in-loop
        await sleep(frameMs);
      }
    } finally {
      try {
        await restoreStored({ ...snapshot, runId: ctx.runId });
      } catch {
        /* giữ bản lưu: lần nối lại hoặc trò sau sẽ trả hình */
      }
      liveRuns.delete(ctx.runId);
    }
  };

  const runTransform = async (
    ctx: Ctx,
    ev: LiveEffectEvent,
    sourceName: string
  ) => {
    const { currentProgramSceneName: sceneName } = await call(
      'GetCurrentProgramScene'
    );
    let sceneItemId: number;
    try {
      ({ sceneItemId } = await call('GetSceneItemId', {
        sceneName,
        sourceName,
      }));
    } catch {
      return 'source_missing' as const;
    }
    const { sceneItemTransform: original } = (await call(
      'GetSceneItemTransform',
      { sceneName, sceneItemId }
    )) as { sceneItemTransform: Transform };
    const c = toCenter(original);
    const { w, h } = sizeOf(c);
    const bounded = isBounded(c);
    const video = ev.code === 'shrink' ? await call('GetVideoSettings') : null;
    const set = (patch: Partial<Transform>) =>
      call('SetSceneItemTransform', {
        sceneName,
        sceneItemId,
        sceneItemTransform: writable({ ...c, ...patch }),
      });
    /** Phóng to, thu nhỏ: có khung bao thì OBS bỏ qua tỉ lệ, phải đổi kích thước khung bao. */
    const scaleBy = (kx: number, ky: number): Partial<Transform> =>
      bounded
        ? {
            boundsWidth: (c.boundsWidth ?? 0) * kx,
            boundsHeight: (c.boundsHeight ?? 0) * ky,
          }
        : { scaleX: c.scaleX * kx, scaleY: c.scaleY * ky };

    await perform(
      ctx,
      ev.durationSec,
      { kind: 'transform', sceneName, sceneItemId, transform: original },
      async () => undefined,
      async (t, s) => {
        switch (ev.code) {
          case 'quake': {
            const amp = 40 * (1 - t * 0.3);
            await set({
              positionX: c.positionX + (Math.random() * 2 - 1) * amp,
              positionY: c.positionY + (Math.random() * 2 - 1) * amp,
            });
            break;
          }
          case 'upside_down':
            await set({ rotation: Math.min(180, s * 600) });
            break;
          case 'mirror':
            // Lật bằng tỉ lệ âm, cả khi có khung bao (khung bao không âm được); OBS lật được thì lật, không thì hình đứng yên.
            await set({ scaleX: -c.scaleX });
            break;
          case 'spin':
            await set({ rotation: (s * 240) % 360 });
            break;
          case 'drunk':
            await set({
              rotation: 8 * Math.sin(s * 2),
              positionX: c.positionX + 30 * Math.sin(s * 1.3),
            });
            break;
          case 'shrink':
            await set({
              ...scaleBy(0.25, 0.25),
              positionX: (video?.baseWidth ?? 1920) - (w * 0.25) / 2 - 40,
              positionY: (video?.baseHeight ?? 1080) - (h * 0.25) / 2 - 40,
            });
            break;
          case 'zoom': {
            const k = s % 2 < 1.5 ? 1.8 : 1;
            await set(scaleBy(k, k));
            break;
          }
          case 'stretch':
            // Khung bao kiểu giữ tỉ lệ (scale inner/outer) thì hình chỉ to nhỏ, không méo.
            await set(scaleBy(1.6, 0.6));
            break;
          default:
            break;
        }
      }
    );
    return 'ok' as const;
  };

  const runFilter = async (
    ctx: Ctx,
    ev: LiveEffectEvent,
    sourceName: string
  ) => {
    try {
      await call('GetSourceFilter', { sourceName, filterName: FILTER_NAME });
    } catch {
      try {
        await call('CreateSourceFilter', {
          sourceName,
          filterName: FILTER_NAME,
          filterKind: 'color_filter_v2',
          filterSettings: {},
        });
      } catch {
        return 'source_missing' as const;
      }
    }
    const settings = (extra: Record<string, unknown>) =>
      call('SetSourceFilterSettings', {
        sourceName,
        filterName: FILTER_NAME,
        overlay: false,
        filterSettings: { saturation: 0, hue_shift: 0, opacity: 1, ...extra },
      });
    const start: Record<string, unknown> =
      ev.code === 'grayscale'
        ? { saturation: -1 }
        : ev.code === 'blackout'
        ? { opacity: 0 }
        : {};
    await perform(
      ctx,
      ev.durationSec,
      { kind: 'filter', sourceName },
      async () => {
        // Luôn đặt lại thông số (cầu vồng bắt đầu từ màu gốc, không dính thông số trò trước).
        await settings(start);
        await call('SetSourceFilterEnabled', {
          sourceName,
          filterName: FILTER_NAME,
          filterEnabled: true,
        });
      },
      async (_t, s) => {
        if (ev.code === 'rainbow')
          await settings({ hue_shift: ((s * 360) % 360) - 180 });
      }
    );
    return 'ok' as const;
  };

  const run: ObsRunner['run'] = async (ev, overlay: LiveEffectOverlay) => {
    generation += 1;
    const gen = generation;
    runSeq += 1;
    const ctx: Ctx = {
      runId: `${now()}-${runSeq}`,
      aborted: () => generation !== gen,
    };
    // Bản sót lại mà trả không được thì không chạy trò (tránh lưu nhầm hình đang méo làm bản gốc), báo như thiếu nguồn.
    try {
      await restoreLeftover();
    } catch {
      return 'source_missing';
    }
    const code = ev.code ?? '';
    if (TRANSFORM_CODES.includes(code) || FILTER_CODES.includes(code)) {
      if (!overlay.obsSourceName) return 'source_missing';
      return TRANSFORM_CODES.includes(code)
        ? runTransform(ctx, ev, overlay.obsSourceName)
        : runFilter(ctx, ev, overlay.obsSourceName);
    }
    if (code === 'mute_mic') {
      const inputName = overlay.obsMicName;
      if (!inputName) return 'source_missing';
      let wasMuted: boolean;
      try {
        ({ inputMuted: wasMuted } = await call('GetInputMute', { inputName }));
      } catch {
        return 'source_missing';
      }
      await perform(
        ctx,
        ev.durationSec,
        { kind: 'mute', inputName, wasMuted },
        () => call('SetInputMute', { inputName, inputMuted: true })
      );
      return 'ok';
    }
    if (code === 'penalty_scene') {
      const sceneName = overlay.obsPenaltySceneName;
      if (!sceneName) return 'source_missing';
      const { currentProgramSceneName } = await call('GetCurrentProgramScene');
      let switchFailed = false;
      try {
        // Lệnh chuyển cảnh lỗi vẫn có thể đã chuyển: bản lưu cảnh cũ đã có, phần trả hình đưa cảnh cũ về.
        await perform(
          ctx,
          ev.durationSec,
          { kind: 'scene', sceneName: currentProgramSceneName },
          () =>
            call('SetCurrentProgramScene', { sceneName }).catch((e) => {
              switchFailed = true;
              throw e;
            })
        );
      } catch (e) {
        if (switchFailed) return 'source_missing';
        throw e;
      }
      return 'ok';
    }
    return 'source_missing';
  };

  /** Dừng trò đang chạy (nó tự trả hình lần nữa khi thoát) và trả hình theo bản lưu ngay. */
  const reset = async () => {
    generation += 1;
    await restoreSnapshot();
  };

  return {
    run,
    reset,
    restoreSnapshot,
    connected: () => (opts.isConnected ? opts.isConnected() : true),
  };
};
